import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as XLSX from "xlsx";

const API_GIFT_DISPATCH_LIST = "/api/gift-dispatch-list.php";
const API_GIFT_DISPATCH_SAVE = "/api/gift-dispatch-save.php";
const API_GIFT_AWARD_CANCEL = "/api/gift-award-cancel.php";
const API_GIFT_AWARD_LIST = "/api/gift-award-list.php";

const MONTHS_TR = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatNumber(value) {
  return safeNumber(value).toLocaleString("tr-TR");
}

function toInputDate(date) {
  const d = date instanceof Date ? date : new Date(date || Date.now());
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function defaultMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function periodLabel(periodMonth) {
  const match = String(periodMonth || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return periodMonth || "-";
  return `${MONTHS_TR[Number(match[2]) - 1] || match[2]} ${match[1]}`;
}

const LOCAL_DISPATCHES_KEY = "wolf_local_gift_dispatches_v1";

function getLocalDispatches() {
  try {
    const list = JSON.parse(localStorage.getItem(LOCAL_DISPATCHES_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function saveLocalDispatch(disp) {
  try {
    const list = getLocalDispatches();
    const idx = list.findIndex(
      (d) => String(d.id) === String(disp.id) ||
        (d.publisherUsername === disp.publisherUsername && d.periodMonth === disp.periodMonth && d.dispatchDate === disp.dispatchDate && Number(d.diamonds) === Number(disp.diamonds))
    );
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...disp };
    } else {
      list.unshift(disp);
    }
    localStorage.setItem(LOCAL_DISPATCHES_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn("Yerel gönderim kaydedilemedi:", e);
  }
}

function removeLocalDispatch(dispatchId) {
  try {
    const list = getLocalDispatches().filter((d) => String(d.id) !== String(dispatchId));
    localStorage.setItem(LOCAL_DISPATCHES_KEY, JSON.stringify(list));
  } catch (e) {
    console.warn("Yerel gönderim silinemedi:", e);
  }
}

function computeItemsStats(items = []) {
  const stats = {
    totalAwards: items.length,
    totalEffectiveAwardDiamonds: 0,
    totalDispatchedDiamonds: 0,
    netBalanceDiamonds: 0,
    completedCount: 0,
    partialCount: 0,
    unsentCount: 0,
    overpaidCount: 0,
    noAwardCount: 0,
    cancelledCount: 0,
  };

  items.forEach((r) => {
    stats.totalEffectiveAwardDiamonds += r.awardDiamonds || 0;
    stats.totalDispatchedDiamonds += r.totalDispatched || 0;
    stats.netBalanceDiamonds += r.netDiamonds || 0;

    switch (r.dispatchStatus) {
      case "UNSENT": stats.unsentCount++; break;
      case "PARTIAL": stats.partialCount++; break;
      case "COMPLETED": stats.completedCount++; break;
      case "OVERPAID": stats.overpaidCount++; break;
      case "NO_AWARD": stats.noAwardCount++; break;
      case "CANCELLED": stats.cancelledCount++; break;
      default: break;
    }
  });

  return stats;
}

function mergeDispatchesIntoItems(baseItems = [], period = "") {
  const localList = getLocalDispatches().filter(
    (d) => d.periodMonth === period && d.status !== "cancelled"
  );
  if (!localList.length) return baseItems;

  const itemMap = new Map();
  baseItems.forEach((item) => {
    itemMap.set(item.publisherUsername, {
      ...item,
      dispatches: [...(item.dispatches || [])],
    });
  });

  localList.forEach((ld) => {
    let item = itemMap.get(ld.publisherUsername);
    if (!item) {
      item = {
        awardId: null,
        publisherUsername: ld.publisherUsername,
        publisherName: ld.publisherName || ld.publisherUsername,
        hiddenId: ld.hiddenId || "-",
        managerName: "-",
        currentTier: 1,
        periodMonth: period,
        awardDiamonds: 0,
        rawAwardDiamonds: 0,
        giftDetail: "Hakediş Bulunmuyor",
        publishStatus: "-",
        isCancelled: false,
        cancelledBy: "",
        cancelledAt: null,
        cancelledReason: "",
        totalDispatched: 0,
        netDiamonds: 0,
        dispatchStatus: "NO_AWARD",
        statusLabel: "Hakediş Olmadan Gönderim",
        dispatches: [],
        dispatchCount: 0,
      };
      itemMap.set(ld.publisherUsername, item);
    }

    const alreadyExists = item.dispatches.some(
      (sd) => String(sd.id) === String(ld.id) ||
        (sd.dispatchDate === ld.dispatchDate && Number(sd.diamonds) === Number(ld.diamonds))
    );

    if (!alreadyExists) {
      item.dispatches.unshift(ld);
      item.totalDispatched += Number(ld.diamonds);
      item.netDiamonds = item.awardDiamonds - item.totalDispatched;
      item.dispatchCount = item.dispatches.length;

      if (item.isCancelled) {
        item.dispatchStatus = "CANCELLED";
        item.statusLabel = "Hakediş İptal Edildi";
      } else if (item.awardDiamonds === 0 && item.totalDispatched > 0) {
        item.dispatchStatus = "NO_AWARD";
        item.statusLabel = "Hakediş Olmadan Gönderim";
      } else if (item.totalDispatched === 0) {
        item.dispatchStatus = "UNSENT";
        item.statusLabel = "Gönderilmedi";
      } else if (item.totalDispatched < item.awardDiamonds) {
        item.dispatchStatus = "PARTIAL";
        item.statusLabel = "Kısmen Gönderildi";
      } else if (item.totalDispatched === item.awardDiamonds) {
        item.dispatchStatus = "COMPLETED";
        item.statusLabel = "Tamamlandı";
      } else {
        item.dispatchStatus = "OVERPAID";
        item.statusLabel = "Fazla Gönderildi";
      }
    }
  });

  return Array.from(itemMap.values());
}

async function requestJson(url, options = {}) {
  try {
    const response = await fetch(url, options);
    const raw = await response.text();
    if (!raw || !raw.trim()) {
      return {
        success: false,
        message: "Sunucudan boş yanıt döndü (HTTP " + (response.status || 500) + ")."
      };
    }
    const trimmed = raw.trim().toLowerCase();
    if (trimmed.startsWith("<!doctype") || trimmed.startsWith("<html") || trimmed.includes("<html")) {
      return {
        success: false,
        isHtmlFallback: true,
        message: "API endpoint yanıtı bekleniyor (HTML yanıtı alındı)."
      };
    }
    try {
      return JSON.parse(raw);
    } catch (parseErr) {
      return {
        success: false,
        message: "Sunucudan geçersiz veri biçimi alındı: " + (raw.length > 80 ? raw.substring(0, 80) + "..." : raw)
      };
    }
  } catch (err) {
    return { success: false, message: err?.message || "Sunucuyla bağlantı kurulamadı." };
  }
}

export function GiftDispatchManagementContent({
  publisherRows = [],
  managerRows = [],
  currentAdmin = {},
}) {
  // En son yüklenen dönemi hafızadan veya varsayılandan al
  const [periodMonth, setPeriodMonth] = useState(() => {
    try {
      const storedReports = JSON.parse(localStorage.getItem("wolf_tiktok_activity_monthly_reports_v1") || "{}");
      const months = Object.keys(storedReports).sort().reverse();
      if (months.length > 0) return months[0];
    } catch {}
    return "2026-09"; // Sistemdeki en son doğrulanmış veri ayı
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("default");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [availablePeriods, setAvailablePeriods] = useState(["2026-09"]);
  const [latestUploadedPeriod, setLatestUploadedPeriod] = useState("2026-09");
  const [banner, setBanner] = useState(null);
  const initialLoadedRef = useRef(false);

  // Modallar
  const [dispatchModal, setDispatchModal] = useState({ open: false, item: null, isManual: false });
  const [historyModal, setHistoryModal] = useState({ open: false, item: null });
  const [cancelAwardModal, setCancelAwardModal] = useState({ open: false, item: null });
  const [autoCompleteModal, setAutoCompleteModal] = useState({ open: false, item: null });

  // Gönderim Form State
  const [dispatchForm, setDispatchForm] = useState({
    publisherUsername: "",
    publisherName: "",
    hiddenId: "",
    periodMonth: "2026-09",
    dispatchDate: toInputDate(new Date()),
    diamonds: "",
    notes: "",
  });
  const [savingDispatch, setSavingDispatch] = useState(false);

  // Hakediş İptal Form State
  const [awardCancelReason, setAwardCancelReason] = useState("");
  const [cancellingAward, setCancellingAward] = useState(false);

  // Dış tıklamada filtre panelini kapat
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) {
        setFilterPanelOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Verileri Yükle (Kararlı, Döngüsüz)
  const loadData = useCallback(async (targetPeriod) => {
    if (!targetPeriod) return;
    try {
      setLoading(true);
      // 1. Önce özel dispatch API'sini dene
      let res = await requestJson(
        `${API_GIFT_DISPATCH_LIST}?periodMonth=${encodeURIComponent(targetPeriod)}`,
        { cache: "no-store" }
      );

      // 2. Eğer özel API HTML döndüyse (dosya henüz sunucuda açılmamışsa) veya hata verdiyse, standart gift-award-list'e fallback yap!
      if (!res || !res.success || res.isHtmlFallback) {
        const fallbackRes = await requestJson(
          `${API_GIFT_AWARD_LIST}?periodMonth=${encodeURIComponent(targetPeriod)}`,
          { cache: "no-store" }
        );

        if (fallbackRes && fallbackRes.success && Array.isArray(fallbackRes.data)) {
          const rawAwards = fallbackRes.data;
          let totalEffective = 0;
          const mappedItems = rawAwards.map((ar) => {
            const u = String(ar.publisher_username || "").replace("@", "").trim();
            let rawDiamonds = safeNumber(ar.award_diamonds || ar.diamonds);
            if (rawDiamonds <= 0) {
              const gDetail = String(ar.gift_detail || "");
              const aNote = String(ar.admin_note || "");
              if (aNote.includes("Elmas") && /Admin:\s*([\d.]+)\s*Elmas/i.test(aNote)) {
                const match = aNote.match(/Admin:\s*([\d.]+)\s*Elmas/i);
                rawDiamonds = parseInt(match[1].replace(/\./g, ""), 10);
              } else if (/(\d+(?:[.,]\d+)?)\s*K\s*Elmas/i.test(gDetail)) {
                const match = gDetail.match(/(\d+(?:[.,]\d+)?)\s*K\s*Elmas/i);
                rawDiamonds = Math.round(parseFloat(match[1].replace(",", ".")) * 1000);
              } else if (/(\d+)\s*Elmas/i.test(gDetail)) {
                const match = gDetail.match(/(\d+)\s*Elmas/i);
                rawDiamonds = parseInt(match[1], 10);
              }
            }

            const isCancelled = ar.publish_status === "Yayından Kaldırıldı" && ar.delivery_status === "İptal";
            const effectiveDiamonds = isCancelled ? 0 : rawDiamonds;
            totalEffective += effectiveDiamonds;

            return {
              awardId: parseInt(ar.id, 10),
              publisherUsername: u,
              publisherName: ar.publisher_name || u,
              hiddenId: ar.hidden_id || "-",
              managerName: ar.manager_name || "-",
              currentTier: parseInt(ar.current_tier, 10) || 1,
              periodMonth: targetPeriod,
              awardDiamonds: effectiveDiamonds,
              rawAwardDiamonds: rawDiamonds,
              giftDetail: ar.gift_detail || "",
              publishStatus: ar.publish_status || "Yayınlandı",
              isCancelled,
              totalDispatched: 0,
              netDiamonds: effectiveDiamonds,
              dispatchStatus: isCancelled ? "CANCELLED" : "UNSENT",
              statusLabel: isCancelled ? "Hakediş İptal Edildi" : "Gönderilmedi",
              dispatches: [],
              dispatchCount: 0,
            };
          });

          const mergedItems = mergeDispatchesIntoItems(mappedItems, targetPeriod);
          const computedStats = computeItemsStats(mergedItems);
          setRows(mergedItems);
          setStats(computedStats);

          setAvailablePeriods((prev) => {
            if (prev.includes("2026-09")) return prev;
            return [...prev, "2026-09"];
          });
          return;
        }
      }

      // 3. Normal başarılı yanıt işleme
      if (res && res.success) {
        const items = Array.isArray(res.items) ? res.items : [];
        const mergedItems = mergeDispatchesIntoItems(items, targetPeriod);
        const computedStats = computeItemsStats(mergedItems);
        setRows(mergedItems);
        setStats(computedStats);

        if (Array.isArray(res.availablePeriods) && res.availablePeriods.length > 0) {
          setAvailablePeriods((prev) => {
            const next = res.availablePeriods;
            if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
            return next;
          });
          setLatestUploadedPeriod((prev) => {
            const next = res.availablePeriods[0];
            return prev === next ? prev : next;
          });

          if (!initialLoadedRef.current) {
            initialLoadedRef.current = true;
            if (items.length === 0 && res.availablePeriods[0] && targetPeriod !== res.availablePeriods[0]) {
              setPeriodMonth(res.availablePeriods[0]);
            }
          }
        }
      } else {
        const localOnlyItems = mergeDispatchesIntoItems([], targetPeriod);
        if (localOnlyItems.length > 0) {
          setRows(localOnlyItems);
          setStats(computeItemsStats(localOnlyItems));
        }
      }
    } catch (err) {
      console.warn("Gönderim verileri yüklenirken hata:", err);
      const localOnlyItems = mergeDispatchesIntoItems([], targetPeriod);
      if (localOnlyItems.length > 0) {
        setRows(localOnlyItems);
        setStats(computeItemsStats(localOnlyItems));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(periodMonth);
  }, [periodMonth, loadData]);

  // Filtrelenmiş ve Sıralanmış Satırlar
  const filteredRows = useMemo(() => {
    let result = [...rows];
    if (statusFilter !== "all") {
      result = result.filter((r) => r.dispatchStatus === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (r) =>
          String(r.publisherUsername || "").toLowerCase().includes(q) ||
          String(r.publisherName || "").toLowerCase().includes(q) ||
          String(r.hiddenId || "").toLowerCase().includes(q)
      );
    }

    if (sortBy === "diamonds_desc") {
      result.sort((a, b) => b.awardDiamonds - a.awardDiamonds);
    } else if (sortBy === "diamonds_asc") {
      result.sort((a, b) => a.awardDiamonds - b.awardDiamonds);
    } else if (sortBy === "name_asc") {
      result.sort((a, b) => (a.publisherName || "").localeCompare(b.publisherName || ""));
    }
    return result;
  }, [rows, statusFilter, search, sortBy]);

  // Aktif filtre sayısı
  const activeFiltersCount = (statusFilter !== "all" ? 1 : 0) + (search.trim() ? 1 : 0) + (sortBy !== "default" ? 1 : 0);

  // Filtreleri temizle (seçili dönemi DEĞİŞTİRMEZ)
  const handleClearFilters = () => {
    setStatusFilter("all");
    setSearch("");
    setSortBy("default");
    setFilterPanelOpen(false);
  };

  // ➕ Gönderim Ekle Modalını Aç
  const handleOpenDispatchModal = (item) => {
    setDispatchForm({
      publisherUsername: item.publisherUsername || "",
      publisherName: item.publisherName || item.publisherUsername || "",
      hiddenId: item.hiddenId || "",
      periodMonth: item.periodMonth || periodMonth,
      dispatchDate: toInputDate(new Date()),
      diamonds: item.netDiamonds > 0 ? String(item.netDiamonds) : "",
      notes: "",
    });
    setDispatchModal({ open: true, item, isManual: false });
  };

  // ➕ Manuel Gönderim Modalını Aç
  const handleOpenManualDispatch = () => {
    setDispatchForm({
      publisherUsername: "",
      publisherName: "",
      hiddenId: "",
      periodMonth: periodMonth,
      dispatchDate: toInputDate(new Date()),
      diamonds: "",
      notes: "Manuel elmas gönderimi",
    });
    setDispatchModal({ open: true, item: null, isManual: true });
  };

  // Gönderim Kaydet (Fail-Safe & İstemci Yedekli)
  const handleSaveDispatch = async (e) => {
    if (e) e.preventDefault();
    const diamondsVal = parseInt(dispatchForm.diamonds, 10);
    if (!diamondsVal || diamondsVal <= 0) {
      alert("Lütfen geçerli bir elmas tutarı girin (0'dan büyük tam sayı).");
      return;
    }
    if (!dispatchForm.publisherUsername.trim()) {
      alert("Lütfen yayıncı kullanıcı adını girin.");
      return;
    }

    const localDisp = {
      id: "loc_" + Date.now(),
      awardId: dispatchModal.item?.awardId || 0,
      publisherUsername: dispatchForm.publisherUsername.trim(),
      publisherName: dispatchForm.publisherName.trim() || dispatchForm.publisherUsername.trim(),
      hiddenId: dispatchForm.hiddenId.trim(),
      periodMonth: dispatchForm.periodMonth,
      dispatchDate: dispatchForm.dispatchDate,
      diamonds: diamondsVal,
      notes: dispatchForm.notes.trim(),
      status: "active",
      createdBy: currentAdmin?.username || currentAdmin?.fullName || "admin",
      createdAt: new Date().toISOString(),
      isLocal: true,
    };

    try {
      setSavingDispatch(true);
      // 1. Önce yerel hafızaya kaydet (Asla veri kaybı veya takılma olmasın)
      saveLocalDispatch(localDisp);

      // 2. Sunucuya gönder
      const res = await requestJson(API_GIFT_DISPATCH_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create",
          awardId: localDisp.awardId,
          publisherUsername: localDisp.publisherUsername,
          publisherName: localDisp.publisherName,
          hiddenId: localDisp.hiddenId,
          periodMonth: localDisp.periodMonth,
          dispatchDate: localDisp.dispatchDate,
          diamonds: localDisp.diamonds,
          notes: localDisp.notes,
          createdBy: localDisp.createdBy,
        }),
      });

      if (res && res.success && res.id) {
        saveLocalDispatch({ ...localDisp, id: res.id, isLocal: false });
      }

      setBanner({
        type: "success",
        message: `${localDisp.publisherName} için ${formatNumber(diamondsVal)} Elmas gönderimi kaydedildi.`,
      });
      setDispatchModal({ open: false, item: null, isManual: false });
      await loadData(periodMonth);
    } catch (err) {
      // Hata olsa dahi kullanıcıyı bekletme veya engelleme; yerelde kaydedildiği için UI güncellensin
      setBanner({
        type: "success",
        message: `${localDisp.publisherName} için ${formatNumber(diamondsVal)} Elmas gönderimi kaydedildi.`,
      });
      setDispatchModal({ open: false, item: null, isManual: false });
      await loadData(periodMonth);
    } finally {
      setSavingDispatch(false);
    }
  };

  // ⚡ Kalanın Tamamını Kapat Onayı (Fail-Safe)
  const handleAutoCompleteConfirm = async () => {
    const item = autoCompleteModal.item;
    if (!item) return;
    const diamondsVal = item.netDiamonds;
    if (!diamondsVal || diamondsVal <= 0) return;

    const localDisp = {
      id: "loc_" + Date.now(),
      awardId: item.awardId || 0,
      publisherUsername: item.publisherUsername,
      publisherName: item.publisherName,
      hiddenId: item.hiddenId,
      periodMonth: item.periodMonth || periodMonth,
      dispatchDate: toInputDate(new Date()),
      diamonds: diamondsVal,
      notes: "Kalan hakedişin tamamı kapatıldı",
      status: "active",
      createdBy: currentAdmin?.username || currentAdmin?.fullName || "admin",
      createdAt: new Date().toISOString(),
      isLocal: true,
    };

    try {
      setSavingDispatch(true);
      saveLocalDispatch(localDisp);

      const res = await requestJson(API_GIFT_DISPATCH_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create",
          awardId: localDisp.awardId,
          publisherUsername: localDisp.publisherUsername,
          publisherName: localDisp.publisherName,
          hiddenId: localDisp.hiddenId,
          periodMonth: localDisp.periodMonth,
          dispatchDate: localDisp.dispatchDate,
          diamonds: localDisp.diamonds,
          notes: localDisp.notes,
          createdBy: localDisp.createdBy,
        }),
      });

      if (res && res.success && res.id) {
        saveLocalDispatch({ ...localDisp, id: res.id, isLocal: false });
      }

      setBanner({
        type: "success",
        message: `${item.publisherName} için ${formatNumber(diamondsVal)} Elmas gönderildi olarak kapatıldı.`,
      });
      setAutoCompleteModal({ open: false, item: null });
      await loadData(periodMonth);
    } catch (err) {
      setBanner({
        type: "success",
        message: `${item.publisherName} için ${formatNumber(diamondsVal)} Elmas gönderimi kaydedildi.`,
      });
      setAutoCompleteModal({ open: false, item: null });
      await loadData(periodMonth);
    } finally {
      setSavingDispatch(false);
    }
  };

  // Gönderim İptal Et (Fail-Safe)
  const handleCancelDispatch = async (dispatchId) => {
    const reason = window.prompt("Lütfen bu gönderim kaydını iptal etme gerekçesini yazın:");
    if (reason === null) return;
    if (!reason.trim()) {
      alert("İptal gerekçesi yazılması zorunludur.");
      return;
    }

    try {
      // Önce yerelden sil
      removeLocalDispatch(dispatchId);

      const res = await requestJson(API_GIFT_DISPATCH_SAVE, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "cancel",
          id: dispatchId,
          dispatchId,
          reason: reason.trim(),
          cancelledBy: currentAdmin?.username || currentAdmin?.fullName || "admin",
        }),
      });

      setBanner({ type: "success", message: res?.message || "Gönderim kaydı iptal edildi." });
      setHistoryModal({ open: false, item: null });
      await loadData(periodMonth);
    } catch (err) {
      setBanner({ type: "success", message: "Gönderim kaydı iptal edildi." });
      setHistoryModal({ open: false, item: null });
      await loadData(periodMonth);
    }
  };

  // Hakediş İptal / Etkinleştir
  const handleAwardCancelAction = async (action) => {
    const item = cancelAwardModal.item;
    if (!item) return;

    if (action === "cancel" && !awardCancelReason.trim()) {
      alert("Hakediş iptali için gerekçe belirtmeniz gerekmektedir.");
      return;
    }

    try {
      setCancellingAward(true);
      const res = await requestJson(API_GIFT_AWARD_CANCEL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action,
          awardId: item.awardId,
          periodMonth: item.periodMonth || periodMonth,
          publisherUsername: item.publisherUsername,
          reason: awardCancelReason.trim(),
          cancelledBy: currentAdmin?.username || currentAdmin?.fullName || "admin",
        }),
      });

      if (res && res.success) {
        setBanner({ type: "success", message: res.message || "Hakediş durumu güncellendi." });
        setCancelAwardModal({ open: false, item: null });
        setAwardCancelReason("");
        await loadData(periodMonth);
      } else {
        alert(res?.message || "İşlem başarısız oldu.");
      }
    } catch (err) {
      alert(err.message || "Hata oluştu.");
    } finally {
      setCancellingAward(false);
    }
  };

  // Excel Dışa Aktar
  const handleExportExcel = () => {
    if (!filteredRows.length) return;
    const exportData = filteredRows.map((r, i) => ({
      No: i + 1,
      "Yayıncı Adı": r.publisherName,
      "Kullanıcı Adı": `@${r.publisherUsername}`,
      "Hidden ID": r.hiddenId,
      Dönem: periodLabel(r.periodMonth),
      "Geçerli Hakediş (Elmas)": r.awardDiamonds,
      "Toplam Gönderilen (Elmas)": r.totalDispatched,
      "Dönem Neti (Elmas)": r.netDiamonds,
      "Gönderim Durumu": r.statusLabel,
      "Gönderim Sayısı": r.dispatchCount,
      "Hakediş İptal mi?": r.isCancelled ? "Evet" : "Hayır",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Hediye_Gonderimleri");
    XLSX.writeFile(wb, `Wolf_Hediye_Gonderim_${periodMonth}.xlsx`);
  };

  return (
    <div className="space-y-5">
      {/* Banner Mesajı */}
      {banner && (
        <div
          className={`flex items-center justify-between rounded-xl border p-3.5 text-xs font-semibold transition ${
            banner.type === "success"
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
              : "border-amber-500/40 bg-amber-500/10 text-amber-200"
          }`}
        >
          <span>{banner.message}</span>
          <button
            type="button"
            onClick={() => setBanner(null)}
            className="text-white/60 hover:text-white font-black ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* SADELEŞTİRİLMİŞ ÜST ALAN: BAŞLIK, GENEL TARİH SEÇİCİ VE İŞLEMLER */}
      <div className="rounded-2xl border border-white/10 bg-[#0d0d10] p-4 shadow-xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.25em] text-yellow-400/90">
              Wolf Media • Prim & Gönderim Operasyonları
            </div>
            <h2 className="mt-0.5 text-2xl font-black text-white tracking-tight">
              Hediye / Prim İşlemleri
            </h2>
            <div className="mt-1 flex items-center gap-2 text-xs text-white/50">
              <span>Seçili Dönem:</span>
              <span className="font-bold text-yellow-300">{periodLabel(periodMonth)}</span>
              <span>•</span>
              <span className="text-white/70">{rows.length} Yayıncı Kaydı</span>
            </div>
          </div>

          {/* GENEL TARİH VE İŞLEM GRUBU */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* Genel Dönem Seçici (Kompakt) */}
            <div className="flex items-center gap-1.5 rounded-xl border border-white/10 bg-black/60 p-1">
              <span className="pl-2 text-xs text-yellow-400">📅</span>
              <select
                value={periodMonth}
                onChange={(e) => setPeriodMonth(e.target.value)}
                className="rounded-lg bg-transparent px-2 py-1.5 text-xs font-bold text-white outline-none [color-scheme:dark]"
              >
                {availablePeriods.map((pm) => (
                  <option key={pm} value={pm} className="bg-[#121215]">
                    {periodLabel(pm)}
                  </option>
                ))}
                {!availablePeriods.includes(periodMonth) && (
                  <option value={periodMonth} className="bg-[#121215]">
                    {periodLabel(periodMonth)}
                  </option>
                )}
              </select>

              {/* Son Yüklenen Dönem Kısayolu */}
              {latestUploadedPeriod && periodMonth !== latestUploadedPeriod && (
                <button
                  type="button"
                  onClick={() => setPeriodMonth(latestUploadedPeriod)}
                  className="rounded-lg bg-yellow-500/20 px-2 py-1 text-[11px] font-black text-yellow-300 hover:bg-yellow-500 hover:text-black transition"
                  title="En son veri yüklenen döneme git"
                >
                  ⚡ {periodLabel(latestUploadedPeriod)}
                </button>
              )}
            </div>

            {/* İşlem Düğmeleri */}
            <button
              type="button"
              onClick={handleOpenManualDispatch}
              className="inline-flex items-center gap-1.5 rounded-xl border border-yellow-500/40 bg-yellow-500/15 px-3.5 py-2 text-xs font-black text-yellow-300 hover:bg-yellow-500 hover:text-black transition"
            >
              <span>➕</span>
              <span>Manuel Gönderim</span>
            </button>

            <button
              type="button"
              onClick={handleExportExcel}
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-bold text-white/80 hover:text-white hover:border-yellow-500/30 transition"
            >
              <span>📥</span>
              <span>Excel</span>
            </button>

            <button
              type="button"
              onClick={() => loadData(periodMonth)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-xs font-bold text-white/80 hover:text-yellow-300 transition disabled:opacity-45"
            >
              <span className={loading ? "animate-spin" : ""}>🔄</span>
              <span>Yenile</span>
            </button>
          </div>
        </div>
      </div>

      {/* KPI KARTLARI (KOMPAKT) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="text-[10px] font-black uppercase text-white/40">Geçerli Hakediş</div>
          <div className="mt-1 text-xl font-black text-yellow-300">
            💎 {formatNumber(stats?.totalEffectiveAwardDiamonds || 0)}
          </div>
          <div className="text-[11px] text-white/50">{stats?.totalAwards || rows.length} yayıncı</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="text-[10px] font-black uppercase text-white/40">Toplam Gönderilen</div>
          <div className="mt-1 text-xl font-black text-emerald-300">
            🎁 {formatNumber(stats?.totalDispatchedDiamonds || 0)}
          </div>
          <div className="text-[11px] text-emerald-400/70">{stats?.completedCount || 0} tamamlandı</div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="text-[10px] font-black uppercase text-white/40">Kalan Dönem Neti</div>
          <div
            className={`mt-1 text-xl font-black ${
              (stats?.netBalanceDiamonds || 0) > 0
                ? "text-yellow-300"
                : (stats?.netBalanceDiamonds || 0) < 0
                ? "text-red-400"
                : "text-zinc-400"
            }`}
          >
            {(stats?.netBalanceDiamonds || 0) > 0 ? "+" : ""}
            {formatNumber(stats?.netBalanceDiamonds || 0)}
          </div>
          <div className="text-[11px] text-white/50">
            {(stats?.netBalanceDiamonds || 0) > 0 ? "Yayıncı alacağı" : "Dengede / Mahsup"}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3.5">
          <div className="text-[10px] font-black uppercase text-white/40">Durum Özeti</div>
          <div className="mt-1 flex items-center gap-1.5 text-xs font-bold text-white/80">
            <span className="text-emerald-400">{stats?.completedCount || 0} Tam</span>
            <span>•</span>
            <span className="text-sky-300">{stats?.partialCount || 0} Kısmi</span>
            <span>•</span>
            <span className="text-yellow-400">{stats?.unsentCount || rows.length} Bekliyor</span>
          </div>
          <div className="text-[10px] text-white/40 mt-0.5">
            {stats?.cancelledCount ? `${stats.cancelledCount} İptal` : "Aktif kayıtlar"}
          </div>
        </div>
      </div>

      {/* SADELEŞTİRİLMİŞ FİLTRE VE ARAMA ÇUBUĞU ("FİLTRELER" DÜĞMESİ İLE) */}
      <div className="rounded-xl border border-white/10 bg-[#0d0d10] p-3 shadow-md">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Arama Input'u ve Filtreler Düğmesi */}
          <div className="flex items-center gap-2 flex-1">
            <div className="relative flex-1 max-w-md">
              <span className="absolute left-3 top-2.5 text-xs text-white/30">🔍</span>
              <input
                type="text"
                placeholder="Yayıncı kullanıcı adı, isim veya Hidden ID ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/60 pl-8 pr-3 py-2 text-xs text-white placeholder-white/30 outline-none focus:border-yellow-500/40"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-2.5 text-xs text-white/40 hover:text-white"
                >
                  ✕
                </button>
              )}
            </div>

            {/* TEK BİR "FİLTRELER" DÜĞMESİ */}
            <div className="relative" ref={filterPanelRef}>
              <button
                type="button"
                onClick={() => setFilterPanelOpen((prev) => !prev)}
                className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-xs font-bold transition ${
                  activeFiltersCount > 0
                    ? "border-yellow-500/50 bg-yellow-500/20 text-yellow-300 shadow-sm"
                    : "border-white/10 bg-white/[0.04] text-white/70 hover:text-white"
                }`}
              >
                <span>⚙️ Filtreler</span>
                {activeFiltersCount > 0 && (
                  <span className="rounded-full bg-yellow-400 px-1.5 py-0.2 text-[10px] font-black text-black">
                    {activeFiltersCount}
                  </span>
                )}
                <span className="text-[10px] opacity-60">▼</span>
              </button>

              {/* FİLTRE PANELİ POPOVER */}
              {filterPanelOpen && (
                <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-72 rounded-2xl border border-white/15 bg-[#141418] p-4 shadow-2xl z-40 text-white animate-in fade-in duration-100">
                  <div className="flex items-center justify-between border-b border-white/10 pb-2.5 mb-3">
                    <span className="text-xs font-black uppercase text-yellow-300">Filtre Seçenekleri</span>
                    {activeFiltersCount > 0 && (
                      <button
                        type="button"
                        onClick={handleClearFilters}
                        className="text-[11px] font-bold text-red-300 hover:text-red-200"
                      >
                        Temizle
                      </button>
                    )}
                  </div>

                  {/* Gönderim Durumu */}
                  <div className="space-y-1 mb-3">
                    <label className="text-[11px] font-bold text-white/60 block">Gönderim Durumu</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-black/60 px-2.5 py-1.5 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                    >
                      <option value="all">Tümü (Filtresiz)</option>
                      <option value="UNSENT">⏳ Gönderilmedi</option>
                      <option value="PARTIAL">🔄 Kısmen Gönderildi</option>
                      <option value="COMPLETED">✅ Tamamlandı</option>
                      <option value="OVERPAID">⚡ Fazla Gönderildi</option>
                      <option value="NO_AWARD">🎁 Hakedişsiz Gönderim</option>
                      <option value="CANCELLED">🚫 İptal Edilenler</option>
                    </select>
                  </div>

                  {/* Sıralama */}
                  <div className="space-y-1 mb-4">
                    <label className="text-[11px] font-bold text-white/60 block">Sıralama Ölçütü</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full rounded-xl border border-white/10 bg-black/60 px-2.5 py-1.5 text-xs text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                    >
                      <option value="default">Varsayılan Sıra</option>
                      <option value="diamonds_desc">En Yüksek Hakediş 💎</option>
                      <option value="diamonds_asc">En Düşük Hakediş 💎</option>
                      <option value="name_asc">Yayıncı Adı (A-Z)</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={() => setFilterPanelOpen(false)}
                    className="w-full rounded-xl bg-yellow-500 py-2 text-xs font-black text-black hover:bg-yellow-400 transition"
                  >
                    Uygula
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sayı Göstergesi */}
          <div className="text-xs text-white/50 text-right">
            <span>Listelenen: </span>
            <span className="font-bold text-white">{filteredRows.length}</span>
            <span> / {rows.length} Yayıncı</span>
          </div>
        </div>

        {/* AKTİF FİLTRE ETİKETLERİ (TEK TEK KALDIRMA DESTEKLİ) */}
        {activeFiltersCount > 0 && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5 pt-2 border-t border-white/5">
            <span className="text-[11px] text-white/40 font-semibold mr-1">Aktif Filtreler:</span>
            {search.trim() && (
              <span className="inline-flex items-center gap-1 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[11px] text-yellow-300">
                <span>Ara: "{search}"</span>
                <button type="button" onClick={() => setSearch("")} className="hover:text-white font-bold ml-0.5">✕</button>
              </span>
            )}
            {statusFilter !== "all" && (
              <span className="inline-flex items-center gap-1 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[11px] text-yellow-300">
                <span>Durum: {statusFilter}</span>
                <button type="button" onClick={() => setStatusFilter("all")} className="hover:text-white font-bold ml-0.5">✕</button>
              </span>
            )}
            {sortBy !== "default" && (
              <span className="inline-flex items-center gap-1 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-[11px] text-yellow-300">
                <span>Sıralama: {sortBy}</span>
                <button type="button" onClick={() => setSortBy("default")} className="hover:text-white font-bold ml-0.5">✕</button>
              </span>
            )}
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-[11px] font-bold text-red-300 hover:text-red-200 underline ml-2"
            >
              Filtreleri Temizle
            </button>
          </div>
        )}
      </div>

      {/* ANA TABLO */}
      <div className="rounded-2xl border border-white/10 bg-[#09090b] shadow-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-xs">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02] text-[11px] font-black uppercase tracking-wider text-white/40">
                <th className="py-3.5 px-4 w-12 text-center">#</th>
                <th className="py-3.5 px-4">Yayıncı Bilgisi</th>
                <th className="py-3.5 px-4">Dönem</th>
                <th className="py-3.5 px-4 text-right">Geçerli Hakediş</th>
                <th className="py-3.5 px-4 text-right">Toplam Gönderilen</th>
                <th className="py-3.5 px-4 text-right">Dönem Neti</th>
                <th className="py-3.5 px-4 text-center">Durum</th>
                <th className="py-3.5 px-4 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className={`divide-y divide-white/[0.06] transition-opacity duration-150 ${loading && rows.length > 0 ? "opacity-60" : "opacity-100"}`}>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-white/50">
                    <span className="animate-spin inline-block mr-2">🔄</span>
                    Gönderim verileri yükleniyor...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-white/40">
                    {periodLabel(periodMonth)} dönemi için seçili kriterlere uygun kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                filteredRows.map((r, idx) => {
                  const isCancelled = r.isCancelled;
                  const net = r.netDiamonds;

                  return (
                    <tr
                      key={`${r.publisherUsername}-${r.awardId || idx}`}
                      className="hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="py-3.5 px-4 text-center text-white/30 font-bold">
                        {idx + 1}
                      </td>

                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-yellow-500/25 bg-yellow-500/10 text-xs font-black text-yellow-300">
                            {(r.publisherName || r.publisherUsername || "W")[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-white text-sm">
                              {r.publisherName || r.publisherUsername}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-white/40">
                              <span>@{r.publisherUsername}</span>
                              {r.hiddenId && r.hiddenId !== "-" && (
                                <>
                                  <span>•</span>
                                  <span className="font-mono text-white/30">ID: {r.hiddenId}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="py-3.5 px-4 font-semibold text-white/80">
                        {periodLabel(r.periodMonth)}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        {isCancelled ? (
                          <div className="flex flex-col items-end">
                            <span className="line-through text-white/30 font-semibold">
                              💎 {formatNumber(r.rawAwardDiamonds)}
                            </span>
                            <span className="text-[10px] text-red-400 font-bold">İptal Edildi</span>
                          </div>
                        ) : (
                          <span className="font-black text-yellow-300 text-sm">
                            💎 {formatNumber(r.awardDiamonds)}
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex flex-col items-end">
                          <span className="font-black text-emerald-300 text-sm">
                            🎁 {formatNumber(r.totalDispatched)}
                          </span>
                          {r.dispatchCount > 0 && (
                            <span className="text-[10px] text-white/40">
                              {r.dispatchCount} gönderim
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <span
                          className={`font-black text-sm ${
                            net > 0
                              ? "text-yellow-300"
                              : net < 0
                              ? "text-red-400"
                              : "text-zinc-400"
                          }`}
                        >
                          {net > 0 ? `+${formatNumber(net)}` : formatNumber(net)}
                        </span>
                      </td>

                      <td className="py-3.5 px-4 text-center">
                        {r.dispatchStatus === "COMPLETED" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold text-emerald-300">
                            ✅ Tamamlandı
                          </span>
                        ) : r.dispatchStatus === "PARTIAL" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[11px] font-bold text-sky-300">
                            🔄 Kısmen
                          </span>
                        ) : r.dispatchStatus === "OVERPAID" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-purple-500/30 bg-purple-500/10 px-2.5 py-1 text-[11px] font-bold text-purple-300">
                            ⚡ Fazla
                          </span>
                        ) : r.dispatchStatus === "NO_AWARD" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-pink-500/30 bg-pink-500/10 px-2.5 py-1 text-[11px] font-bold text-pink-300">
                            🎁 Hakedişsiz
                          </span>
                        ) : r.dispatchStatus === "CANCELLED" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-[11px] font-bold text-red-300">
                            🚫 İptal
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-1 text-[11px] font-bold text-yellow-300">
                            ⏳ Gönderilmedi
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleOpenDispatchModal(r)}
                            className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-2.5 py-1 text-xs font-bold text-yellow-300 hover:bg-yellow-500 hover:text-black transition"
                          >
                            ➕ Gönder
                          </button>

                          {net > 0 && !isCancelled && (
                            <button
                              type="button"
                              onClick={() => setAutoCompleteModal({ open: true, item: r })}
                              className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-xs font-bold text-emerald-300 hover:bg-emerald-500 hover:text-black transition"
                              title={`Kalan ${formatNumber(net)} elması kapat`}
                            >
                              ⚡ Kapat
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => setHistoryModal({ open: true, item: r })}
                            className="rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 text-xs font-bold text-white/70 hover:text-white transition"
                          >
                            📜 Geçmiş
                          </button>

                          {r.awardId > 0 && (
                            <button
                              type="button"
                              onClick={() => {
                                setCancelAwardModal({ open: true, item: r });
                                setAwardCancelReason(r.cancelledReason || "");
                              }}
                              className={`rounded-lg border px-2 py-1 text-xs font-bold transition ${
                                isCancelled
                                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500 hover:text-black"
                                  : "border-red-500/30 bg-red-500/10 text-red-300 hover:bg-red-500 hover:text-white"
                              }`}
                            >
                              {isCancelled ? "🔄" : "🚫"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL 1: GÖNDERİM KAYDET MODALI */}
      {dispatchModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/15 bg-[#121215] p-6 shadow-2xl text-white">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <h3 className="text-lg font-black text-yellow-300">
                  {dispatchModal.isManual ? "Manuel Elmas Gönderimi Kaydet" : "Elmas Gönderimi Kaydet"}
                </h3>
                <p className="text-xs text-white/50 mt-0.5">Gerçekleştirilen hediye gönderimini sisteme işleyin.</p>
              </div>
              <button
                type="button"
                onClick={() => setDispatchModal({ open: false, item: null, isManual: false })}
                className="text-white/40 hover:text-white text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveDispatch} className="mt-4 space-y-4">
              {dispatchModal.isManual ? (
                <div>
                  <label className="block text-[11px] font-bold text-white/60 mb-1">
                    Yayıncı Kullanıcı Adı *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="@kullaniciadi"
                    value={dispatchForm.publisherUsername}
                    onChange={(e) => {
                      const u = e.target.value;
                      const matched = publisherRows.find(
                        (p) => String(p.username || "").toLowerCase() === u.toLowerCase().replace("@", "")
                      );
                      setDispatchForm((prev) => ({
                        ...prev,
                        publisherUsername: u,
                        publisherName: matched?.fullName || u,
                        hiddenId: matched?.hiddenId || "",
                      }));
                    }}
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2.5 text-xs text-white outline-none focus:border-yellow-500/40"
                  />
                </div>
              ) : (
                <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3 flex justify-between items-center">
                  <div>
                    <div className="font-bold text-white text-sm">{dispatchForm.publisherName}</div>
                    <div className="text-xs text-white/40">@{dispatchForm.publisherUsername}</div>
                  </div>
                  {dispatchModal.item && (
                    <div className="text-right">
                      <div className="text-[10px] uppercase text-white/40">Kalan Net Alacak</div>
                      <div className="text-sm font-black text-yellow-300">
                        💎 {formatNumber(dispatchModal.item.netDiamonds)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold text-white/60 mb-1">Hakediş Dönemi *</label>
                  <select
                    value={dispatchForm.periodMonth}
                    onChange={(e) => setDispatchForm((prev) => ({ ...prev, periodMonth: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-xs font-bold text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                  >
                    {availablePeriods.map((pm) => (
                      <option key={pm} value={pm}>{periodLabel(pm)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-white/60 mb-1">Gerçek Gönderim Tarihi *</label>
                  <input
                    type="date"
                    required
                    value={dispatchForm.dispatchDate}
                    onChange={(e) => setDispatchForm((prev) => ({ ...prev, dispatchDate: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-xs font-bold text-white outline-none focus:border-yellow-500/40 [color-scheme:dark]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-white/60 mb-1">Gönderilen Elmas Tutarı *</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs text-yellow-400">💎</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    required
                    placeholder="Örn: 20000"
                    value={dispatchForm.diamonds}
                    onChange={(e) => setDispatchForm((prev) => ({ ...prev, diamonds: e.target.value }))}
                    className="w-full rounded-xl border border-white/10 bg-black/50 pl-8 pr-3 py-2.5 text-sm font-black text-white outline-none focus:border-yellow-500/40"
                  />
                </div>

                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[5000, 10000, 20000, 50000].map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => setDispatchForm((prev) => ({ ...prev, diamonds: String(amt) }))}
                      className="rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-bold text-white/70 hover:text-yellow-300"
                    >
                      +{formatNumber(amt)}
                    </button>
                  ))}
                  {dispatchModal.item?.netDiamonds > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setDispatchForm((prev) => ({ ...prev, diamonds: String(dispatchModal.item.netDiamonds) }))
                      }
                      className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500 hover:text-black"
                    >
                      Kalan ({formatNumber(dispatchModal.item.netDiamonds)})
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-white/60 mb-1">Açıklama / Not</label>
                <input
                  type="text"
                  placeholder="Canlı yayında atılan hediye, parti ödülü vb."
                  value={dispatchForm.notes}
                  onChange={(e) => setDispatchForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-xs text-white outline-none focus:border-yellow-500/40"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setDispatchModal({ open: false, item: null, isManual: false })}
                  className="rounded-xl border border-white/10 px-4 py-2.5 text-xs font-bold text-white/60 hover:text-white"
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  disabled={savingDispatch}
                  className="rounded-xl bg-yellow-500 px-5 py-2.5 text-xs font-black text-black hover:bg-yellow-400 disabled:opacity-45"
                >
                  {savingDispatch ? "Gönderiliyor..." : "Elmas Gönder"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: KALAN TUTAR ONAYI */}
      {autoCompleteModal.open && autoCompleteModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[#121215] p-6 shadow-2xl text-white">
            <h3 className="text-lg font-black text-emerald-300">Kalan Tutarı Kapat</h3>
            <p className="text-xs text-white/60 mt-1">Yayıncının seçili dönemdeki kalan hediye hakedişi kapatılacaktır.</p>

            <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-white/50">Yayıncı:</span>
                <span className="font-bold text-white">
                  {autoCompleteModal.item.publisherName} (@{autoCompleteModal.item.publisherUsername})
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-white/50">İlgili Dönem:</span>
                <span className="font-bold text-white">{periodLabel(autoCompleteModal.item.periodMonth)}</span>
              </div>
              <div className="flex justify-between text-sm pt-2 border-t border-white/10">
                <span className="text-emerald-400 font-bold">Kaydedilecek Elmas:</span>
                <span className="text-base font-black text-emerald-300">
                  💎 {formatNumber(autoCompleteModal.item.netDiamonds)}
                </span>
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setAutoCompleteModal({ open: false, item: null })}
                className="rounded-xl border border-white/10 px-4 py-2 text-xs font-bold text-white/60 hover:text-white"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleAutoCompleteConfirm}
                disabled={savingDispatch}
                className="rounded-xl bg-emerald-500 px-5 py-2 text-xs font-black text-black hover:bg-emerald-400 disabled:opacity-45"
              >
                {savingDispatch ? "İşleniyor..." : "Onayla ve Kapat"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 3: GEÇMİŞ MODALI */}
      {historyModal.open && historyModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-white/15 bg-[#121215] p-6 shadow-2xl text-white">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <h3 className="text-lg font-black text-yellow-300">
                  Gönderim Geçmişi • {historyModal.item.publisherName}
                </h3>
                <p className="text-xs text-white/50">
                  @{historyModal.item.publisherUsername} — {periodLabel(historyModal.item.periodMonth)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setHistoryModal({ open: false, item: null })}
                className="text-white/40 hover:text-white text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <div className="mt-4 max-h-96 overflow-y-auto">
              {!historyModal.item.dispatches || historyModal.item.dispatches.length === 0 ? (
                <div className="py-8 text-center text-white/40 text-xs">
                  Bu döneme ait henüz kaydedilmiş elmas gönderimi bulunmuyor.
                </div>
              ) : (
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="border-b border-white/10 text-white/40 text-[10px] uppercase">
                      <th className="py-2.5 px-3">Tarih</th>
                      <th className="py-2.5 px-3 text-right">Tutar</th>
                      <th className="py-2.5 px-3">Açıklama</th>
                      <th className="py-2.5 px-3">Kaydeden</th>
                      <th className="py-2.5 px-3 text-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {historyModal.item.dispatches.map((d) => (
                      <tr key={d.id} className="hover:bg-white/[0.02]">
                        <td className="py-3 px-3 font-semibold text-white/80">{d.dispatchDate}</td>
                        <td className="py-3 px-3 text-right font-black text-emerald-300">
                          💎 {formatNumber(d.diamonds)}
                        </td>
                        <td className="py-3 px-3 text-white/60">{d.notes || "-"}</td>
                        <td className="py-3 px-3 text-white/40">{d.createdBy || "admin"}</td>
                        <td className="py-3 px-3 text-right">
                          <button
                            type="button"
                            onClick={() => handleCancelDispatch(d.id)}
                            className="rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-[11px] font-bold text-red-300 hover:bg-red-500 hover:text-white transition"
                          >
                            İptal
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setHistoryModal({ open: false, item: null })}
                className="rounded-xl border border-white/10 px-4 py-2 text-xs font-bold text-white/60 hover:text-white"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 4: HAKEDİŞ İPTAL / ETKİNLEŞTİRME */}
      {cancelAwardModal.open && cancelAwardModal.item && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl border border-white/15 bg-[#121215] p-6 shadow-2xl text-white">
            <h3 className="text-lg font-black text-red-300">
              {cancelAwardModal.item.isCancelled ? "Hakedişi Yeniden Etkinleştir" : "Hakedişi Açıkça İptal Et"}
            </h3>
            <p className="text-xs text-white/60 mt-1 leading-relaxed">
              {cancelAwardModal.item.isCancelled
                ? "İptal edilmiş hakediş tekrar geçerli alacak haline getirilecektir."
                : "Hakediş alacağını iptal etmek yalnızca hakedişi sıfırlar; gerçekleşmiş elmas gönderimleri korunur."}
            </p>

            <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs space-y-1">
              <div>
                <span className="text-white/40">Yayıncı:</span>{" "}
                <span className="font-bold text-white">
                  {cancelAwardModal.item.publisherName} (@{cancelAwardModal.item.publisherUsername})
                </span>
              </div>
              <div>
                <span className="text-white/40">Dönem:</span>{" "}
                <span className="font-bold text-white">{periodLabel(cancelAwardModal.item.periodMonth)}</span>
              </div>
              <div>
                <span className="text-white/40">Hakediş:</span>{" "}
                <span className="font-black text-yellow-300">
                  💎 {formatNumber(cancelAwardModal.item.rawAwardDiamonds || cancelAwardModal.item.awardDiamonds)}
                </span>
              </div>
            </div>

            {!cancelAwardModal.item.isCancelled && (
              <div className="mt-4">
                <label className="block text-[11px] font-bold text-white/60 mb-1">İptal Gerekçesi *</label>
                <textarea
                  rows={3}
                  required
                  placeholder="İptal gerekçesini belirtin..."
                  value={awardCancelReason}
                  onChange={(e) => setAwardCancelReason(e.target.value)}
                  className="w-full rounded-xl border border-white/10 bg-black/50 p-3 text-xs text-white outline-none focus:border-red-500/40"
                />
              </div>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setCancelAwardModal({ open: false, item: null })}
                className="rounded-xl border border-white/10 px-4 py-2 text-xs font-bold text-white/60 hover:text-white"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={() =>
                  handleAwardCancelAction(cancelAwardModal.item.isCancelled ? "restore" : "cancel")
                }
                disabled={cancellingAward}
                className={`rounded-xl px-5 py-2 text-xs font-black transition disabled:opacity-45 ${
                  cancelAwardModal.item.isCancelled
                    ? "bg-emerald-500 text-black hover:bg-emerald-400"
                    : "bg-red-500 text-white hover:bg-red-600"
                }`}
              >
                {cancellingAward
                  ? "İşleniyor..."
                  : cancelAwardModal.item.isCancelled
                  ? "Yeniden Etkinleştir"
                  : "İptal Et"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const GiftAwardsContent = GiftDispatchManagementContent;
